import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight } from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";
import ProductCard from "../components/ProductCard";
import { categories, testimonials, features, blogs } from "../mock";
import { apiGetProducts, apiGetBanners, resolveImg } from "../api";

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-8">
    <h2 className="font-serif text-3xl sm:text-4xl text-neutral-900 tracking-wide">{children}</h2>
    {sub && <p className="mt-2 text-sm text-neutral-500">{sub}</p>}
    <div className="mt-4 mx-auto h-[2px] w-16 bg-gradient-to-r from-[#FFE29A] to-[#C8932F]" />
  </div>
);

const Skeletons = ({ n = 4 }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
    {[...Array(n)].map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="aspect-[3/4] bg-neutral-200 rounded-sm" />
        <div className="h-3 bg-neutral-200 rounded mt-3 w-1/2" />
        <div className="h-4 bg-neutral-200 rounded mt-2 w-3/4" />
      </div>
    ))}
  </div>
);

const Bestsellers = ({ allProducts }) => {
  const tabs = [
    { key: "men", label: "For Him" },
    { key: "women", label: "For Her" },
    { key: "attars", label: "Attars" },
    { key: "gift-sets", label: "Gifting" },
  ];
  const [active, setActive] = useState("men");

  const filtered = allProducts.filter((p) =>
    active === "men" || active === "women" ? p.gender === active && p.category === "perfumes" : p.category === active
  );

  return (
    <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-16">
      <SectionTitle sub="Our most loved fragrances, chosen by you">Our Bestsellers</SectionTitle>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-5 py-2 text-sm font-medium tracking-wide rounded-full border transition-all ${
              active === t.key
                ? "bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] border-transparent"
                : "bg-transparent text-neutral-700 border-neutral-300 hover:border-[#C8932F]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {allProducts.length === 0 ? (
        <Skeletons />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {filtered.slice(0, 4).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </section>
  );
};

const CategoryGrid = () => (
  <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-12">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {categories.map((c) => (
        <Link key={c.label} to={c.to} className="group relative overflow-hidden rounded-sm aspect-square">
          <img src={c.image} alt={c.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 text-center">
            <h3 className="font-serif text-xl sm:text-2xl text-[#F3E7D8]">{c.label}</h3>
            <span className="mt-1 inline-flex items-center gap-1 text-xs text-[#FFE29A] tracking-widest uppercase">
              Shop Now <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const PromoBanner = () => (
  <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-6">
    <div className="relative overflow-hidden rounded-sm">
      <img
        src="https://images.unsplash.com/photo-1754578645612-850d520385a8"
        alt="Discover luxury gifting"
        className="h-[260px] sm:h-[340px] w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050304]/85 to-transparent flex items-center">
        <div className="px-8 lg:px-16">
          <p className="text-[#FFE29A] text-xs tracking-[0.3em] uppercase">Limited Edition</p>
          <h3 className="font-serif text-3xl sm:text-4xl text-[#F3E7D8] mt-3 max-w-sm leading-tight">
            Discover Luxury Gifting
          </h3>
          <Link
            to="/collections/gift-sets"
            className="mt-6 inline-block bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-7 py-3 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity"
          >
            Explore Now
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const NewArrivals = ({ allProducts }) => {
  const arrivals = allProducts.filter((p) => (p.badges || []).includes("New")).concat(allProducts.slice(0, 4)).slice(0, 4);
  return (
    <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-16">
      <SectionTitle sub="Fresh from the Deegford atelier">New Arrivals</SectionTitle>
      {allProducts.length === 0 ? (
        <Skeletons />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {arrivals.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </section>
  );
};

const Features = () => (
  <section className="bg-[#050304]">
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
      {features.map((f) => (
        <div key={f.title}>
          <h3 className="font-serif text-2xl text-[#F3E7D8]">{f.title}</h3>
          <p className="mt-2 text-sm text-[#E8B9C4]">{f.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-[#f6f3ee] py-16">
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
      <SectionTitle>From Our Loved Ones</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t) => (
          <div key={t.name} className="bg-white rounded-sm overflow-hidden shadow-sm">
            <img src={t.image} alt={t.name} className="h-60 w-full object-cover" />
            <div className="p-6">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#C8932F] text-[#C8932F]" />
                ))}
              </div>
              <p className="text-sm text-neutral-700 leading-relaxed italic">"{t.quote}"</p>
              <p className="mt-4 font-semibold text-neutral-900">{t.name}</p>
              <p className="text-xs text-[#C8932F]">{t.role}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Blogs = () => (
  <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-16">
    <SectionTitle sub="Fragrance guides & stories">From Our Journal</SectionTitle>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {blogs.map((b) => (
        <div key={b.title} className="group cursor-pointer">
          <div className="overflow-hidden rounded-sm aspect-[4/3]">
            <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <h4 className="mt-4 font-serif text-lg text-neutral-900 leading-snug group-hover:text-[#C8932F] transition-colors">
            {b.title}
          </h4>
          <span className="mt-2 inline-flex items-center gap-1 text-xs text-[#C8932F] tracking-widest uppercase">
            {b.link} <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      ))}
    </div>
  </section>
);

const Home = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    apiGetProducts().then(setAllProducts).catch(() => setAllProducts([]));
    apiGetBanners()
      .then((bs) => setBanners(bs.map((b) => ({ ...b, image: resolveImg(b.image) }))))
      .catch(() => setBanners([]));
  }, []);

  return (
    <div>
      <HeroCarousel slides={banners} />
      <Bestsellers allProducts={allProducts} />
      <CategoryGrid />
      <PromoBanner />
      <NewArrivals allProducts={allProducts} />
      <Features />
      <Testimonials />
      <Blogs />
    </div>
  );
};

export default Home;
